import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Send, AlertTriangle, CheckCircle, Users } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../../contexts/AuthContext";
import { firebaseAuth } from "../../services/firebase-auth";

interface FormData {
    targetUserId: string;
    title: string;
    body: string;
    type: "marketing" | "update" | "activity";
    customData: string;
}

export const AdminNotificationTest: React.FC = () => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const [formData, setFormData] = useState<FormData>({
        targetUserId: "",
        title: "",
        body: "",
        type: "activity",
        customData: ""
    });

    const [isSending, setIsSending] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [result, setResult] = useState<any>(null);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value
        });
    };

    const sendNotification = async () => {
        if (!formData.targetUserId || !formData.title || !formData.body) {
            setError("Todos los campos son obligatorios");
            return;
        }

        setIsSending(true);
        setSuccess(false);
        setError(null);
        setResult(null);

        try {
            // Obtener el token de autenticación
            const idToken = await firebaseAuth.getIdToken();

            if (!idToken) {
                throw new Error("No se pudo obtener el token de autenticación");
            }

            let data = {};
            if (formData.customData) {
                try {
                    data = JSON.parse(formData.customData);
                } catch (e) {
                    data = { customMessage: formData.customData };
                }
            }

            // Enviar solicitud al endpoint
            const response = await fetch(`${import.meta.env.VITE_API_URL}/api/notifications/send/user`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${idToken}`
                },
                body: JSON.stringify({
                    targetUserId: formData.targetUserId,
                    title: formData.title,
                    body: formData.body,
                    type: formData.type,
                    data
                })
            });

            const responseData = await response.json();

            if (!response.ok) {
                throw new Error(responseData.error || "Error al enviar la notificación");
            }

            setSuccess(true);
            setResult(responseData);

            // Resetear formulario después de éxito
            setFormData({
                ...formData,
                title: "",
                body: "",
                customData: ""
            });

            // Ocultar mensaje de éxito después de 5 segundos
            setTimeout(() => {
                setSuccess(false);
            }, 5000);
        } catch (err) {
            console.error("Error al enviar notificación:", err);
            setError(err instanceof Error ? err.message : "Error desconocido");
        } finally {
            setIsSending(false);
        }
    };

    return (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center mb-4">
                <Users className="mr-2 text-gray-500 dark:text-gray-400" />
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                    Enviar notificación a usuario específico
                </h2>
            </div>

            <div className="text-sm text-yellow-600 dark:text-yellow-400 bg-yellow-50 dark:bg-yellow-900/30 p-3 rounded-md mb-4">
                Esta funcionalidad es solo para administradores o propósitos de prueba.
            </div>

            {/* Alertas */}
            <AnimatePresence>
                {error && (
                    <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-md p-4 mb-4"
                    >
                        <div className="flex">
                            <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-500 mr-2 flex-shrink-0" />
                            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
                        </div>
                    </motion.div>
                )}

                {success && (
                    <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-md p-4 mb-4"
                    >
                        <div className="flex">
                            <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-500 mr-2 flex-shrink-0" />
                            <p className="text-sm text-green-600 dark:text-green-400">
                                Notificación enviada correctamente
                            </p>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Formulario */}
            <div className="space-y-4">
                <div>
                    <label htmlFor="targetUserId" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        ID del usuario destino
                    </label>
                    <input
                        type="text"
                        id="targetUserId"
                        name="targetUserId"
                        value={formData.targetUserId}
                        onChange={handleInputChange}
                        placeholder="UID del usuario"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                    />
                </div>

                <div>
                    <label htmlFor="title" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Título de la notificación
                    </label>
                    <input
                        type="text"
                        id="title"
                        name="title"
                        value={formData.title}
                        onChange={handleInputChange}
                        placeholder="Título"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                    />
                </div>

                <div>
                    <label htmlFor="body" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Cuerpo del mensaje
                    </label>
                    <textarea
                        id="body"
                        name="body"
                        value={formData.body}
                        onChange={handleInputChange}
                        placeholder="Mensaje"
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                    />
                </div>

                <div>
                    <label htmlFor="type" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Tipo de notificación
                    </label>
                    <select
                        id="type"
                        name="type"
                        value={formData.type}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                    >
                        <option value="activity">Actividad</option>
                        <option value="update">Actualización</option>
                        <option value="marketing">Marketing</option>
                    </select>
                </div>

                <div>
                    <label htmlFor="customData" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Datos personalizados (JSON o texto)
                    </label>
                    <textarea
                        id="customData"
                        name="customData"
                        value={formData.customData}
                        onChange={handleInputChange}
                        placeholder='{"key": "value"} o mensaje personalizado'
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                    />
                </div>
            </div>

            {/* Botón de envío */}
            <div className="mt-6">
                <button
                    onClick={sendNotification}
                    disabled={isSending}
                    className={`flex items-center justify-center px-4 py-2 rounded-md text-white bg-blue-600 hover:bg-blue-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
            ${isSending ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                    {isSending ? (
                        <>
                            <div className="animate-spin mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                            Enviando...
                        </>
                    ) : (
                        <>
                            <Send size={18} className="mr-2" />
                            Enviar notificación
                        </>
                    )}
                </button>
            </div>

            {/* Mostrar resultado */}
            {result && (
                <div className="mt-6 bg-gray-50 dark:bg-gray-700 p-4 rounded-md">
                    <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Resultado:</h3>
                    <pre className="text-xs whitespace-pre-wrap break-all text-gray-600 dark:text-gray-300">
                        {JSON.stringify(result, null, 2)}
                    </pre>
                </div>
            )}
        </div>
    );
};

export default AdminNotificationTest;